import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, InstagramIcon, LinkedinIcon, MailIcon, PlusIcon, CheckIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9pYpmBqnTFtudjrgYsyKLh/METRO-BRANDBOOK_Mesa_de_trabajo_1_copia_11.svg';
/* ------------------------------------------------------------------ */
/*  Shared mockup primitives                                           */
/* ------------------------------------------------------------------ */
function BrowserFrame({
  url,
  children
}: {
  url: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-md overflow-hidden border border-[var(--divider)] shadow-lg bg-white" data-id="element-1621">
      <div className="bg-[#F4F5F7] border-b border-[var(--divider)] px-3 py-2 flex items-center gap-3" data-id="element-1622">
        <div className="flex gap-1.5" data-id="element-1623">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" data-id="element-1624" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" data-id="element-1625" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" data-id="element-1626" />
        </div>
        <div className="flex-1 bg-white rounded-sm border border-[var(--divider)] px-2.5 py-0.5 text-[10px] font-mono text-[var(--text-muted)] truncate" data-id="element-1627">
          {url}
        </div>
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-1628">
          v2026.1
        </div>
      </div>
      <div className="bg-white" data-id="element-1629">{children}</div>
    </div>;
}
function SiteNav({
  active
}: {
  active: 'inicio' | 'servicios' | 'packs' | 'nosotros' | 'terms';
}) {
  const items: Array<{
    key: typeof active;
    label: string;
  }> = [{
    key: 'inicio',
    label: 'Inicio'
  }, {
    key: 'servicios',
    label: 'Servicios'
  }, {
    key: 'packs',
    label: 'Packs'
  }, {
    key: 'nosotros',
    label: 'Nosotros'
  }];
  return <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--divider)]" data-id="element-1630">
      <div className="flex items-center gap-6" data-id="element-1631">
        <img src={METRO_LOGO_URL} alt="me—tro" className="h-5 w-auto" data-id="element-1632" />
        <nav className="flex items-center gap-5 text-[10px]" data-id="element-1633">
          {items.map(i => <a key={i.key} className={i.key === active ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]'} data-id="element-1634">
              {i.label}
            </a>)}
          <a className="text-[var(--text-muted)]" data-id="element-1635">Contacto</a>
        </nav>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-1636">
        <span data-id="element-1637">ES / EN</span>
        <button className="px-3 py-1.5 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium" data-id="element-1638">
          Hablemos
        </button>
      </div>
    </div>;
}
function SiteFooter() {
  return <div className="bg-[var(--text-ink)] text-white px-6 py-8 mt-0" data-id="element-1639">
      <div className="grid grid-cols-12 gap-4 mb-6" data-id="element-1640">
        <div className="col-span-6" data-id="element-1641">
          <div className="font-space text-2xl font-bold tracking-tight leading-tight" data-id="element-1642">
            Donde las
            <br data-id="element-1643" />
            ideas conectan.
          </div>
          <div className="text-[10px] opacity-60 mt-2" data-id="element-1644">
            Metro Agency · Barcelona
          </div>
        </div>
        <div className="col-span-2" data-id="element-1645">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1646">
            EXPLORA
          </div>
          <div className="space-y-1 text-[10px]" data-id="element-1647">
            <div data-id="element-1648">Inicio</div>
            <div data-id="element-1649">Servicios</div>
            <div data-id="element-1650">Packs</div>
            <div data-id="element-1651">Nosotros</div>
            <div data-id="element-1652">Contacto</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-1653">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1654">
            ESTUDIO
          </div>
          <div className="space-y-1 text-[10px] opacity-80" data-id="element-1655">
            <div data-id="element-1656">Carrer de la Marina, 145</div>
            <div data-id="element-1657">08013 Barcelona</div>
            <div data-id="element-1658">hello@metroagency.co</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-1659">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-1660">
            SOCIAL
          </div>
          <div className="flex gap-2 mt-1" data-id="element-1661">
            <InstagramIcon className="w-3.5 h-3.5" data-id="element-1662" />
            <LinkedinIcon className="w-3.5 h-3.5" data-id="element-1663" />
            <MailIcon className="w-3.5 h-3.5" data-id="element-1664" />
          </div>
        </div>
      </div>
      <div className="border-t border-white/20 pt-3 flex items-center justify-between text-[9px] opacity-60" data-id="element-1665">
        <span data-id="element-1666">© 2026 Metro Agency S.L. · NIF B12345678</span>
        <span data-id="element-1667">Política de privacidad · Aviso legal · Cookies</span>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  Service network mark — small reusable graphic                      */
/* ------------------------------------------------------------------ */
function ServiceNetwork({
  height = 200
}: {
  height?: number;
}) {
  return <svg viewBox="0 0 600 200" className="w-full" style={{
    height
  }} data-id="element-1668">
      <line x1="20" y1="60" x2="580" y2="60" stroke="#1A2BFF" strokeWidth="3" data-id="element-1669" />
      <line x1="20" y1="100" x2="580" y2="100" stroke="#E8C547" strokeWidth="3" data-id="element-1670" />
      <line x1="20" y1="140" x2="580" y2="140" stroke="#F26A35" strokeWidth="3" data-id="element-1671" />
      <line x1="20" y1="180" x2="580" y2="180" stroke="#2A8E47" strokeWidth="3" data-id="element-1672" />
      {[60, 100, 140, 180].map((y, i) => [120, 240, 360, 480].map((x, j) => <circle key={`${i}-${j}`} cx={x} cy={y} r="6" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="2" data-id="element-1673" />))}
      <text x="20" y="30" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#6B6E78" letterSpacing="1.5" data-id="element-1674">
        STRATEGY · CREATIVE · PRODUCTION · PERFORMANCE
      </text>
    </svg>;
}
/* ================================================================== */
/*  HOME PAGE MOCKUP                                                   */
/* ================================================================== */
function HomePageMockup() {
  return <BrowserFrame url="metroagency.co" data-id="element-1675">
      <SiteNav active="inicio" data-id="element-1676" />

      {/* Hero — full bleed cool grey */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1677">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1678">
          AGENCIA CREATIVA · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1679">
          Donde las
          <br data-id="element-1680" />
          ideas conectan.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1681">
          Conectamos estrategia, creatividad y producción en una sola línea —
          para que las campañas lleguen donde tienen que llegar, sin perderse
          entre proveedores.
        </div>
        <div className="flex items-center gap-3 mt-6" data-id="element-1682">
          <button className="px-4 py-2 bg-[var(--text-ink)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1683">
            Build something that moves people
            <ArrowRightIcon className="w-3 h-3" data-id="element-1684" />
          </button>
          <button className="px-4 py-2 border border-[var(--text-ink)] rounded-full text-xs font-medium" data-id="element-1685">
            Ver trabajos
          </button>
        </div>
        <div className="mt-10" data-id="element-1686">
          <ServiceNetwork height={180} data-id="element-1687" />
        </div>
      </div>

      {/* Service network explainer */}
      <div className="px-6 py-10 bg-white" data-id="element-1688">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1689">
          01 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight max-w-lg mb-6" data-id="element-1690">
          Cuatro líneas. Una red. Cero traspasos.
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-1691">
          {[{
          n: '01',
          t: 'Estrategia',
          d: 'Posicionamiento, audiencias, mensaje. Donde decidimos qué construir y por qué.'
        }, {
          n: '02',
          t: 'Creatividad',
          d: 'Identidad, dirección de arte, narrativa. La idea con la que el resto se conecta.'
        }, {
          n: '03',
          t: 'Producción',
          d: 'Vídeo, fotografía, web, eventos. Donde la idea se vuelve real.'
        }, {
          n: '04',
          t: 'Performance',
          d: 'Paid media, analítica, optimización. Donde medimos si la conexión funcionó.'
        }].map(c => <div key={c.n} className="border border-[var(--divider)] p-4 rounded-sm" data-id="element-1692">
              <div className="font-mono text-[10px] text-[var(--accent-blue)] font-bold mb-2" data-id="element-1693">
                {c.n}
              </div>
              <div className="font-space font-bold text-base mb-1" data-id="element-1694">{c.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1695">
                {c.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* Manifesto callout */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-1696">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-1697">
          MANIFIESTO
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-1698">
          No creemos en magia. Creemos en sistemas que funcionan. Construimos
          infraestructura para ideas que duran — campañas que conectan
          estrategia con ejecución, marcas que mueven personas.
        </div>
        <div className="mt-5 text-[10px] opacity-70 font-mono tracking-widest" data-id="element-1699">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Selected work */}
      <div className="px-6 py-10 bg-white" data-id="element-1700">
        <div className="flex items-end justify-between mb-5" data-id="element-1701">
          <div data-id="element-1702">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1703">
              02 · TRABAJO SELECCIONADO
            </div>
            <div className="font-space font-bold text-2xl tracking-tight" data-id="element-1704">
              Lo último de la línea.
            </div>
          </div>
          <a className="text-[10px] font-medium underline" data-id="element-1705">Ver todo →</a>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-1706">
          {[{
          c: '#1A2BFF',
          t: 'Cliente · Sector',
          n: 'Campaña 360'
        }, {
          c: '#0E0E12',
          t: 'Cliente · Sector',
          n: 'Identidad'
        }, {
          c: '#E8C547',
          t: 'Cliente · Sector',
          n: 'Producción'
        }].map((w, i) => <div key={i} data-id="element-1707">
              <div className="aspect-[4/5] rounded-sm flex items-end p-3" style={{
            background: w.c
          }} data-id="element-1708">
                <div className={'text-[10px] font-bold ' + (w.c === '#E8C547' ? 'text-[var(--text-ink)]' : 'text-white')} data-id="element-1709">
                  {w.n}
                </div>
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-1.5 font-mono tracking-wider" data-id="element-1710">
                {w.t}
              </div>
            </div>)}
        </div>
      </div>

      {/* Clients strip */}
      <div className="px-6 py-6" style={{
      background: '#EDEEF1'
    }} data-id="element-1711">
        <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-3 text-center" data-id="element-1712">
          CONFÍAN EN NOSOTROS
        </div>
        <div className="flex items-center justify-around opacity-50" data-id="element-1713">
          {['CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE'].map((c, i) => <div key={i} className="text-[10px] font-bold tracking-wider" data-id="element-1714">
                {c}
              </div>)}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-1715">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-1716">
          ¿Una idea que conectar?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-1717">
          Cuéntanos el proyecto, el contexto y el plazo. Respondemos en menos de
          un día laborable.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1718">
          Start the conversation
          <ArrowRightIcon className="w-3 h-3" data-id="element-1719" />
        </button>
      </div>

      <SiteFooter data-id="element-1720" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  SERVICES PAGE MOCKUP                                               */
/* ================================================================== */
function ServicesPageMockup() {
  return <BrowserFrame url="metroagency.co/servicios" data-id="element-1721">
      <SiteNav active="servicios" data-id="element-1722" />

      {/* Hero — full bleed blue */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-1723">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-1724">
          SERVICIOS
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1725">
          Una línea.
          <br data-id="element-1726" />
          Cuatro estaciones.
        </div>
        <div className="font-helvetica text-sm opacity-90 leading-relaxed max-w-lg" data-id="element-1727">
          Estrategia, creatividad, producción y performance — integradas de
          principio a fin. Sin externalizar las partes críticas. Sin perder la
          coherencia entre fases.
        </div>
        <div className="mt-8 grid grid-cols-4 gap-2 max-w-2xl" data-id="element-1728">
          {['Estrategia', 'Creatividad', 'Producción', 'Performance'].map((s, i) => <div key={i} className="text-center text-[9px] py-2 rounded-sm border border-white/30" data-id="element-1729">
                <div className="font-mono opacity-70" data-id="element-1730">0{i + 1}</div>
                <div className="font-bold mt-0.5" data-id="element-1731">{s}</div>
              </div>)}
        </div>
      </div>

      {/* Service blocks */}
      <div className="px-6 py-10 bg-white space-y-4" data-id="element-1732">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-1733">
          01 · QUÉ HACEMOS
        </div>

        {[{
        cat: 'CREATIVIDAD',
        num: '01',
        title: 'Identidad, dirección de arte y narrativa',
        desc: 'Construimos sistemas de marca que viajan con todo lo demás. No logos sueltos — sistemas que aguantan el uso real.',
        items: ['Identidad visual y verbal', 'Dirección de arte para campaña', 'Sistemas de diseño', 'Brand guidelines y handover']
      }, {
        cat: 'MARKETING & PERFORMANCE',
        num: '02',
        title: 'Estrategia, datos y optimización',
        desc: 'Estrategia con cabeza de negocio. Paid media, analítica y optimización con foco en el resultado, no en el dashboard.',
        items: ['Estrategia de marca y go-to-market', 'Paid social y search', 'Analítica y atribución', 'CRO y optimización continua']
      }, {
        cat: 'PRODUCCIÓN & EVENTOS',
        num: '03',
        title: 'Vídeo, fotografía, web y experiencias',
        desc: 'Producción in-house de principio a fin. Lo que diseñamos lo producimos — sin traspasos, sin teléfono escacharrado.',
        items: ['Vídeo y fotografía', 'Diseño y desarrollo web', 'Eventos y activaciones', 'Contenido para social y paid']
      }].map(s => <div key={s.num} className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-1734">
            <div className="flex items-start gap-4 p-5 border-b border-[var(--divider)]" data-id="element-1735">
              <div className="flex-shrink-0" data-id="element-1736">
                <div className="font-mono text-[10px] text-[var(--accent-blue)] tracking-widest mb-1 font-bold" data-id="element-1737">
                  {s.cat}
                </div>
                <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none" data-id="element-1738">
                  {s.num}
                </div>
              </div>
              <div className="flex-1" data-id="element-1739">
                <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-1740">
                  {s.title}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-1741">
                  {s.desc}
                </div>
              </div>
              <PlusIcon className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" strokeWidth={1.5} data-id="element-1742" />
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 px-5 py-3 bg-[var(--bg-canvas)]" data-id="element-1743">
              {s.items.map(it => <div key={it} className="flex items-start gap-2 text-[10px]" data-id="element-1744">
                  <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-1745" />
                  <span data-id="element-1746">{it}</span>
                </div>)}
            </div>
          </div>)}
      </div>

      {/* Approach callout */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1747">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-1748">
          02 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1749">
          Hands-on. End-to-end.
          <br data-id="element-1750" />
          Sin agencias intermedias.
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-1751">
          {[{
          t: 'Un solo equipo',
          d: 'El mismo equipo que diseña es el que produce. Coherencia por construcción.'
        }, {
          t: 'Un solo punto de contacto',
          d: 'Hablas con quien decide. No con quien gestiona al que decide.'
        }, {
          t: 'Un solo lenguaje',
          d: 'Lo estratégico, lo creativo y lo técnico hablan el mismo idioma desde el primer día.'
        }].map(b => <div key={b.t} className="bg-white p-4 rounded-sm border border-[var(--divider)]" data-id="element-1752">
              <div className="font-space font-bold text-sm mb-1.5" data-id="element-1753">{b.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1754">
                {b.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-1755">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-1756">
          ¿Planificando una campaña?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-1757">
          Mejor empezar por el mapa. Cuéntanos el objetivo y dibujamos la línea
          contigo.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1758">
          Map your next campaign
          <ArrowRightIcon className="w-3 h-3" data-id="element-1759" />
        </button>
      </div>

      <SiteFooter data-id="element-1760" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  ABOUT PAGE MOCKUP                                                  */
/* ================================================================== */
function AboutPageMockup() {
  return <BrowserFrame url="metroagency.co/nosotros" data-id="element-1761">
      <SiteNav active="nosotros" data-id="element-1762" />

      {/* Hero */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1763">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1764">
          NOSOTROS · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1765">
          Construimos
          <br data-id="element-1766" />
          la infraestructura.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1767">
          Metro nace en Barcelona, de la dificultad de encontrar una agencia
          digital que ejecute la campaña completa sin externalizar las partes
          críticas. Construimos lo que nos hubiera gustado contratar.
        </div>
      </div>

      {/* Dark manifesto */}
      <div className="px-6 py-12 text-white" style={{
      background: '#0E0E12'
    }} data-id="element-1768">
        <div className="font-mono text-[9px] tracking-widest mb-3 text-[var(--accent-blue)]" data-id="element-1769">
          MANIFIESTO · 80 PALABRAS
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-1770">
          <p className="mb-3 font-bold" data-id="element-1771">
            No creemos en magia. Creemos en sistemas que funcionan.
          </p>
          <p className="mb-3 text-base opacity-90 font-normal" data-id="element-1772">
            En un mundo saturado de contenido, construimos infraestructura para
            ideas que duran. Campañas que conectan estrategia con ejecución,
            marcas que mueven personas, historias que merecen la atención.
          </p>
          <p className="text-base opacity-90 font-normal" data-id="element-1773">
            No perseguimos trends. Construimos líneas entre lo que necesitas
            comunicar y quién necesita escucharlo.
          </p>
        </div>
        <div className="mt-6 pt-4 border-t border-white/20 font-mono text-[10px] tracking-widest opacity-70" data-id="element-1774">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Voice */}
      <div className="px-6 py-10 bg-white" data-id="element-1775">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1776">
          01 · CÓMO HABLAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1777">
          Punto. No exclamación.
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-1778">
          <div className="p-4 rounded-sm text-white" style={{
          background: '#1A2BFF'
        }} data-id="element-1779">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-1780">
              USAMOS
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-1781">
              Conectar · construir · red · línea · estrategia · campaña · real ·
              honesto · directo · intencional
            </div>
          </div>
          <div className="p-4 rounded-sm bg-[var(--text-ink)] text-white" data-id="element-1782">
            <div className="font-mono text-[9px] opacity-60 tracking-widest mb-2" data-id="element-1783">
              NUNCA
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-1784">
              Magia · wow factor · rock · synergy · disrupt · journey ·
              game-changer · guru · ninja
            </div>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1785">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-1786">
          02 · EQUIPO
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-1787">
          Las personas conducen el tren.
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-1788">
          {[['Nombre Apellido', 'Strategy & accounts'], ['Nombre Apellido', 'Creative director'], ['Nombre Apellido', 'Production lead'], ['Nombre Apellido', 'Performance & data']].map(([n, r], i) => <div key={i} data-id="element-1789">
              <div className="aspect-square rounded-sm mb-2" style={{
            background: i % 2 === 0 ? '#1A2BFF' : '#0E0E12'
          }} data-id="element-1790" />
              <div className="font-space font-bold text-[11px] leading-tight" data-id="element-1791">
                {n}
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-0.5" data-id="element-1792">
                {r}
              </div>
            </div>)}
        </div>
      </div>

      {/* Numbers */}
      <div className="px-6 py-10 bg-white" data-id="element-1793">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-5" data-id="element-1794">
          03 · ESTACIONES POR LAS QUE HEMOS PASADO
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-1795">
          {[['+50', 'Campañas integradas'], ['+30', 'Marcas con identidad nueva'], ['12', 'Sectores cubiertos'], ['4', 'Idiomas operativos']].map(([n, l]) => <div key={l} className="border-t border-[var(--text-ink)] pt-3" data-id="element-1796">
              <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none mb-1" data-id="element-1797">
                {n}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-tight" data-id="element-1798">
                {l}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12" style={{
      background: '#1A2BFF'
    }} data-id="element-1799">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4 text-white" data-id="element-1800">
          Empezamos por una conversación.
        </div>
        <div className="text-sm text-white/80 mb-5 max-w-md" data-id="element-1801">
          Sin briefing pre-rellenado. Sin formulario de 12 campos. Solo
          cuéntanos el contexto.
        </div>
        <button className="px-5 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-1802">
          Let's connect the dots
          <ArrowRightIcon className="w-3 h-3" data-id="element-1803" />
        </button>
      </div>

      <SiteFooter data-id="element-1804" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  PACKS PAGE MOCKUP                                                  */
/* ================================================================== */
function PriceTag({
  amount,
  highlight = false,
  small = false
}: {
  amount: string;
  highlight?: boolean;
  small?: boolean;
}) {
  return <div className={'inline-flex items-baseline gap-1 ' + (highlight ? 'text-[var(--accent-blue)]' : '')} data-id="element-1805">
      <span className={'font-space font-bold tracking-tight leading-none ' + (small ? 'text-xl' : 'text-3xl')} data-id="element-1806">
        {amount}
      </span>
      <span className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider" data-id="element-1807">
        IVA INCL.
      </span>
    </div>;
}
function PackCard({
  tier,
  tierLabel,
  name,
  tagline,
  price,
  timeline,
  ideal,
  features,
  warnings,
  cta,
  accent,
  savings,
  combo
}: {
  tier: string;
  tierLabel: string;
  name: string;
  tagline: string;
  price: string;
  timeline: string;
  ideal: string;
  features: string[];
  warnings?: string[];
  cta: string;
  accent: 'blue' | 'ink' | 'grey';
  savings?: string;
  combo?: boolean;
}) {
  const bg = accent === 'blue' ? '#1A2BFF' : accent === 'ink' ? '#0E0E12' : '#EDEEF1';
  const fg = accent === 'grey' ? '#0E0E12' : '#FFFFFF';
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-1808">
      {/* Header */}
      <div className="p-5" style={{
      background: bg,
      color: fg
    }} data-id="element-1809">
        <div className="flex items-center justify-between mb-3" data-id="element-1810">
          <div className="font-mono text-[9px] tracking-widest opacity-75" data-id="element-1811">
            {tier} · {tierLabel}
          </div>
          {combo && <div className="text-[8px] font-bold tracking-widest bg-white/15 px-2 py-0.5 rounded-full" data-id="element-1812">
              COMBO
            </div>}
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-1" data-id="element-1813">
          {name}
        </div>
        <div className="text-[11px] opacity-85 leading-relaxed mb-4" data-id="element-1814">
          {tagline}
        </div>
        <div className="flex items-baseline gap-2" data-id="element-1815">
          <span className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-1816">
            {price}
          </span>
          <span className="text-[9px] font-mono tracking-wider opacity-75" data-id="element-1817">
            IVA INCL.
          </span>
        </div>
        {savings && <div className="text-[10px] mt-1 opacity-90 font-medium" data-id="element-1818">
            {savings}
          </div>}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col" data-id="element-1819">
        <div className="grid grid-cols-2 gap-3 pb-3 border-b border-[var(--divider)] mb-3 text-[10px]" data-id="element-1820">
          <div data-id="element-1821">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1822">
              PLAZO
            </div>
            <div className="font-medium" data-id="element-1823">{timeline}</div>
          </div>
          <div data-id="element-1824">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1825">
              IDEAL PARA
            </div>
            <div className="font-medium leading-snug" data-id="element-1826">{ideal}</div>
          </div>
        </div>

        {warnings && warnings.length > 0 && <div className="mb-3" data-id="element-1827">
            <div className="font-mono text-[8px] text-[var(--accent-orange)] tracking-widest mb-2 font-bold" data-id="element-1828">
              LIMITACIONES EXPLÍCITAS
            </div>
            <ul className="space-y-1.5" data-id="element-1829">
              {warnings.map((w, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug text-[var(--text-muted)]" data-id="element-1830">
                  <span className="text-[var(--accent-orange)] flex-shrink-0" data-id="element-1831">
                    ⚠
                  </span>
                  <span data-id="element-1832">{w}</span>
                </li>)}
            </ul>
          </div>}

        <div data-id="element-1833">
          <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1834">
            INCLUYE
          </div>
          <ul className="space-y-1.5" data-id="element-1835">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug" data-id="element-1836">
                <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-1837" />
                <span data-id="element-1838">{f}</span>
              </li>)}
          </ul>
        </div>

        <div className="mt-5 pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-3" data-id="element-1839">
          <button className="flex-1 px-3 py-2 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-1840">
            {cta}
            <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-1841" />
          </button>
          <button className="px-3 py-2 border border-[var(--text-ink)] rounded-full text-[10px] font-medium" data-id="element-1842">
            Agendar
          </button>
        </div>
      </div>
    </div>;
}
function PacksPageMockup() {
  return <BrowserFrame url="metroagency.co/packs" data-id="element-1843">
      <SiteNav active="packs" data-id="element-1844" />

      {/* Hero */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1845">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1846">
          PACKS · PRECIOS CON IVA INCLUIDO
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-1847">
          Cuatro tiers.
          <br data-id="element-1848" />
          Una línea clara.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1849">
          Packs cerrados con alcance, plazo y precio definidos. Sin sorpresas en
          la factura. Todos los precios mostrados incluyen el IVA — lo que ves
          es lo que pagas.
        </div>

        {/* Tier strip */}
        <div className="mt-8 grid grid-cols-4 gap-2 max-w-3xl" data-id="element-1850">
          {[{
          n: 'TIER 0',
          t: 'Micro',
          d: 'Existo online'
        }, {
          n: 'TIER 1',
          t: 'Essentials',
          d: 'Presencia funcional'
        }, {
          n: 'TIER 2',
          t: 'Professional',
          d: 'Negocio visible'
        }, {
          n: 'TIER 3',
          t: 'Premium',
          d: 'Sistema completo'
        }].map((t, i) => <div key={i} className="bg-white rounded-sm p-3 border border-[var(--divider)]" data-id="element-1851">
              <div className="font-mono text-[8px] text-[var(--accent-blue)] tracking-widest mb-1 font-bold" data-id="element-1852">
                {t.n}
              </div>
              <div className="font-space font-bold text-sm leading-tight mb-0.5" data-id="element-1853">
                {t.t}
              </div>
              <div className="text-[9px] text-[var(--text-muted)]" data-id="element-1854">{t.d}</div>
            </div>)}
        </div>
      </div>

      {/* TIER 0 — featured separately */}
      <div className="px-6 py-10 bg-white" data-id="element-1855">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1856">
          <div data-id="element-1857">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1858">
              TIER 0 · LA PUERTA DE ENTRADA
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1859">
              Micro.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1860">
              Para negocios que solo necesitan existir en Google. Pack
              intencionalmente limitado.
            </div>
          </div>
          <div className="text-right" data-id="element-1861">
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none" data-id="element-1862">
              €390
            </div>
            <div className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider mt-1" data-id="element-1863">
              IVA INCLUIDO
            </div>
          </div>
        </div>

        <PackCard tier="TIER 0" tierLabel="MICRO" name="Web Micro" tagline="Existo online. Pack intencionalmente limitado para negocios ultra-pequeños sin presupuesto. La puerta de entrada — no el destino." price="€390" timeline="48–72 horas" ideal="Frutería, peluquería de barrio, taller familiar, autónomos" warnings={['Página única (one-page), scroll vertical, sin navegación', 'Basado en plantilla — elegimos de 3 templates pre-hechos', 'Sin formulario — solo botones de Llamar y WhatsApp', 'Sin Google Maps integrado, sin email profesional', 'Sin hosting gestionado (cliente lo contrata aparte)', '0 revisiones, sin soporte post-entrega']} features={['Secciones básicas: nombre, horario, teléfono, dirección, descripción', 'Responsive (móvil + desktop)', 'Botón WhatsApp directo', 'Hasta 5 imágenes (propias o generadas con IA)', 'SEO básico (título + descripción)']} cta="Comprar Web Micro" accent="ink" data-id="element-1864" />
      </div>

      {/* TIER 1 — Essentials */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1865">
        <div className="mb-5" data-id="element-1866">
          <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1867">
            TIER 1 · PRESENCIA DIGITAL FUNCIONAL
          </div>
          <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1868">
            Essentials.
          </div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1869">
            Para negocios que entienden que necesitan algo más que "solo
            existir".
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-1870">
          <PackCard tier="TIER 1" tierLabel="ESSENTIALS" name="Website Essentials" tagline="Landing funcional con formulario, mapa y SEO básico. La presencia digital mínima viable." price="€990" timeline="5–7 días laborables" ideal="Peluquerías, talleres, fontaneros, electricistas" features={['1–2 secciones (Home + Contacto)', 'Diseño responsive (móvil + desktop)', 'Formulario de contacto funcional', 'Integración con Google Maps', 'SEO básico (meta tags + sitemap)', 'Enlace a redes sociales', 'Hosting gratuito 1er año (luego €60/año)', 'Certificado SSL incluido', '1 ronda de revisión']} cta="Comprar Essentials" accent="blue" data-id="element-1871" />
          <PackCard tier="TIER 1" tierLabel="ESSENTIALS" name="Brand Basics" tagline="Identidad básica rápida. Logo, paleta, tipografía y aplicaciones — listo para arrancar." price="€1.200" timeline="7–10 días laborables" ideal="Startups, nuevos negocios, identidad básica rápido" features={['Logo principal (2 conceptos, 1 ronda)', 'Paleta de colores (3–4 colores)', 'Tipografía seleccionada (1–2 fuentes)', '10 aplicaciones mockup (tarjetas, social, etc.)', 'Brand guidelines PDF básico (5 páginas)', 'Archivos finales (PNG, SVG, PDF)']} cta="Comprar Brand Basics" accent="blue" data-id="element-1872" />
        </div>
      </div>

      {/* TIER 2 — Professional */}
      <div className="px-6 py-10 bg-white" data-id="element-1873">
        <div className="mb-5" data-id="element-1874">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1875">
            TIER 2 · NEGOCIO VISIBLE
          </div>
          <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1876">
            Professional.
          </div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1877">
            Para negocios establecidos con presupuesto moderado — restaurantes,
            tiendas, clínicas.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-1878">
          <PackCard tier="TIER 2" tierLabel="PROFESSIONAL" name="Website Professional" tagline="Sitio completo de 3–4 páginas con galería, optimización y soporte post-entrega." price="€2.400" timeline="10–14 días laborables" ideal="Restaurantes, bares, tiendas, clínicas, estética" features={['Todo lo de Essentials, ampliado', '3–4 páginas (Home, Servicios, Galería/Menú, Contacto)', 'Hasta 20 imágenes optimizadas con IA', 'Google Business Profile optimizado', 'Menú digital / catálogo de productos básico', 'Velocidad de carga optimizada (<2s)', '2 rondas de revisión', '1 mes de soporte post-entrega', 'Capacitación 30 min']} cta="Comprar Professional" accent="ink" data-id="element-1879" />
          <PackCard tier="TIER 2" tierLabel="PROFESSIONAL" name="Brand + Web Combo" tagline="Identidad nueva y sitio que la usa desde el día 1. La opción cuando empiezas todo desde cero." price="€3.500" timeline="14–18 días laborables" ideal="Nuevos negocios que necesitan todo desde cero" savings="Ahorro de €100 vs. comprar separado" combo features={['Brand Basics completo', 'Website Professional completo', 'Web integrada con la identidad nueva desde día 1', 'Coherencia visual y verbal garantizada', 'Un solo equipo, un solo plazo']} cta="Comprar Combo" accent="ink" data-id="element-1880" />
        </div>
      </div>

      {/* TIER 3 — Premium */}
      <div className="px-6 py-10" style={{
      background: '#1A2BFF'
    }} data-id="element-1881">
        <div className="mb-5 text-white" data-id="element-1882">
          <div className="font-mono text-[9px] opacity-75 tracking-widest mb-1" data-id="element-1883">
            TIER 3 · SISTEMA COMPLETO
          </div>
          <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1884">
            Premium.
          </div>
          <div className="text-[11px] opacity-85 mt-1 max-w-md" data-id="element-1885">
            Para empresas serias, e-commerce y negocios con ambición de escalar.
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-1886">
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Website Premium" tagline="5–6 páginas custom, reservas online, blog SEO, animaciones y 3 meses de soporte." price="€4.800" timeline="18–25 días laborables" ideal="E-commerce, servicios premium, negocios que quieren destacar" features={['Todo lo de Professional, ampliado', '5–6 páginas completamente personalizadas', 'Sistema de reservas online', 'Blog funcional con 3 artículos SEO iniciales', 'Integración WhatsApp Business', 'Pop-ups de ofertas / promociones', 'Animaciones y micro-interacciones', '3 rondas de revisión', '3 meses de soporte post-entrega', 'Capacitación 1 hora completa']} cta="Comprar Premium" accent="ink" data-id="element-1887" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Brand Premium" tagline="Identidad completa con estrategia, positioning, sub-marcas y 30+ aplicaciones." price="€5.500" timeline="20–28 días laborables" ideal="B2B, startups tech, marcas que necesitan coherencia total" features={['Todo lo de Brand Basics, ampliado', '3 conceptos de logo (vs. 2)', 'Sub-marca / iconos adicionales', 'Brand strategy workshop (2 horas)', 'Positioning statement + messaging', 'Brand guidelines completo (15–20 páginas)', 'Templates de presentación', 'Email signature design', 'Social media templates', '30+ aplicaciones mockup']} cta="Comprar Brand Premium" accent="ink" data-id="element-1888" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Full System" tagline="Brand + Web + Content. Todo el sistema en un solo proyecto, con ahorro real." price="€8.900" timeline="25–30 días laborables" ideal="Lanzamientos nuevos, rebrands completos" savings="Ahorro de €1.400 vs. comprar separado" combo features={['Brand Premium completo', 'Website Premium completo', 'Content Starter Pack:', '· 3 videos cortos (30–60s) para social/web', '· 40 imágenes profesionales con IA', '· Copywriting para todo el sitio', '· 5 posts de social media listos']} cta="Comprar Full System" accent="blue" data-id="element-1889" />
        </div>
      </div>

      {/* Add-ons */}
      <div className="px-6 py-10 bg-white" data-id="element-1890">
        <div className="flex items-baseline justify-between mb-5" data-id="element-1891">
          <div data-id="element-1892">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1893">
              EXTRA · SERVICIOS ADICIONALES
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-1894">
              Add-ons.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-1895">
              Para clientes actuales o como upsell sobre un pack — añádelos a tu
              pedido o contrátalos por separado.
            </div>
          </div>
        </div>

        <div className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-1896">
          <div className="grid grid-cols-12 bg-[var(--text-ink)] text-white text-[9px] font-bold tracking-widest px-4 py-2.5" data-id="element-1897">
            <div className="col-span-6" data-id="element-1898">SERVICIO</div>
            <div className="col-span-2 text-right" data-id="element-1899">PRECIO</div>
            <div className="col-span-2 text-right" data-id="element-1900">PLAZO</div>
            <div className="col-span-2 text-right" data-id="element-1901">ACCIÓN</div>
          </div>
          {[['Pack imágenes IA (30 imágenes)', '€150', '2–3 días', false], ['Video corto (30–60s, edición simple)', '€350', '5–7 días', false], ['Copywriting (por página)', '€180', '2–3 días', false], ['Email marketing setup (Mailchimp/Klaviyo)', '€250', '3–4 días', false], ['Foto/video shoot local Barcelona', '€800', '1 día', false], ['Gestión redes sociales', '€450/mes', 'Ongoing', true], ['SEO mensual (básico)', '€350/mes', 'Ongoing', true], ['Mantenimiento web mensual', '€90/mes', 'Ongoing', true]].map(([name, price, time, recurring], i) => <div key={i} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--divider)] last:border-b-0 text-[10px] items-center" data-id="element-1902">
              <div className="col-span-6 flex items-center gap-2" data-id="element-1903">
                <span className="font-medium" data-id="element-1904">{name as string}</span>
                {recurring && <span className="text-[8px] font-mono text-[var(--accent-blue)] bg-[var(--bg-canvas)] px-1.5 py-0.5 rounded-full tracking-wider" data-id="element-1905">
                    RECURRENTE
                  </span>}
              </div>
              <div className="col-span-2 text-right font-space font-bold" data-id="element-1906">
                {price as string}
              </div>
              <div className="col-span-2 text-right text-[var(--text-muted)]" data-id="element-1907">
                {time as string}
              </div>
              <div className="col-span-2 text-right" data-id="element-1908">
                <button className="text-[9px] font-medium text-[var(--accent-blue)] underline" data-id="element-1909">
                  Añadir →
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {/* FAQ + Final CTA */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-1910">
        <div className="grid grid-cols-12 gap-6" data-id="element-1911">
          <div className="col-span-7" data-id="element-1912">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1913">
              PREGUNTAS FRECUENTES
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-1914">
              Antes de comprar.
            </div>
            <div className="space-y-2" data-id="element-1915">
              {[['¿Los precios incluyen IVA?', 'Sí. Todos los packs muestran el precio final con IVA incluido. Lo que ves es lo que pagas.'], ['¿Cómo se paga?', '50% al inicio del proyecto y 50% antes de la entrega. Transferencia o tarjeta. Stripe.'], ['¿Y si necesito cambios después?', 'Cada pack incluye las revisiones especificadas. Revisiones extra se cotizan como add-on.'], ['¿Hay garantía?', '14 días de garantía post-entrega para corregir errores del trabajo realizado. Política completa en Términos y Condiciones.'], ['¿Puedo upgradear de un pack a otro?', 'Sí. El importe pagado del pack actual se descuenta del precio del pack superior si lo upgradeas en los primeros 60 días.']].map(([q, a], i) => <div key={i} className="bg-white rounded-sm border border-[var(--divider)] p-4" data-id="element-1916">
                  <div className="flex items-start justify-between gap-3" data-id="element-1917">
                    <div className="font-space font-bold text-sm" data-id="element-1918">{q}</div>
                    <PlusIcon className="w-3.5 h-3.5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-1919" />
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] leading-relaxed mt-2" data-id="element-1920">
                    {a}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="col-span-5" data-id="element-1921">
            <div className="bg-[var(--text-ink)] text-white rounded-sm p-6 sticky top-4" data-id="element-1922">
              <div className="font-mono text-[9px] tracking-widest opacity-70 mb-2" data-id="element-1923">
                ¿NO LO TIENES CLARO?
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-3" data-id="element-1924">
                Empieza por una conversación.
              </div>
              <div className="text-[11px] opacity-80 leading-relaxed mb-4" data-id="element-1925">
                Cuéntanos qué necesitas, en 15 minutos te decimos qué pack
                encaja — o si necesitas uno custom.
              </div>
              <button className="w-full px-4 py-2.5 bg-white text-[var(--text-ink)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2 mb-2" data-id="element-1926">
                Agendar llamada · 15 min
                <ArrowRightIcon className="w-3 h-3" data-id="element-1927" />
              </button>
              <button className="w-full px-4 py-2.5 border border-white/30 rounded-full text-xs font-medium" data-id="element-1928">
                Enviar mensaje
              </button>
              <div className="border-t border-white/20 mt-5 pt-4 text-[9px] opacity-60 font-mono tracking-widest" data-id="element-1929">
                PAGO SEGURO · STRIPE · 50/50
              </div>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter data-id="element-1930" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  TERMS & CONDITIONS PAGE MOCKUP                                     */
/* ================================================================== */
function TermsPageMockup() {
  return <BrowserFrame url="metroagency.co/terminos" data-id="element-1931">
      <SiteNav active="terms" data-id="element-1932" />

      {/* Hero */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-1933">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-1934">
          LEGAL · METRO AGENCY S.L.
        </div>
        <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-1935">
          Términos
          <br data-id="element-1936" />y condiciones.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-1937">
          Las reglas del juego, en castellano y sin letra pequeña confusa. Si
          algo no está claro, pregúntanos antes de firmar.
        </div>
        <div className="mt-4 flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-1938">
          <span className="font-mono tracking-wider" data-id="element-1939">
            ÚLTIMA ACTUALIZACIÓN · [ FECHA ]
          </span>
          <span data-id="element-1940">·</span>
          <span className="font-mono tracking-widest" data-id="element-1941">v1.0</span>
        </div>
      </div>

      {/* Layout: TOC + content */}
      <div className="px-6 py-10 grid grid-cols-12 gap-6" data-id="element-1942">
        {/* TOC */}
        <div className="col-span-4" data-id="element-1943">
          <div className="sticky top-4" data-id="element-1944">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-1945">
              ÍNDICE
            </div>
            <div className="space-y-1.5" data-id="element-1946">
              {['01 · Sobre Metro Agency', '02 · Servicios contratados', '03 · Condiciones de pago', '04 · Plazos y entregas', '05 · Revisiones y cambios', '06 · Política de devoluciones', '07 · Garantía post-entrega', '08 · Propiedad intelectual', '09 · Confidencialidad', '10 · Cancelaciones', '11 · Limitación de responsabilidad', '12 · Protección de datos · RGPD', '13 · Cookies', '14 · Modificaciones', '15 · Jurisdicción aplicable'].map((t, i) => <a key={i} className={'block text-[10px] py-1 ' + (i === 2 ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]')} data-id="element-1947">
                  {t}
                </a>)}
            </div>
            <div className="mt-5 pt-4 border-t border-[var(--divider)]" data-id="element-1948">
              <div className="text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-1949">
                ¿Dudas legales?
                <br data-id="element-1950" />
                <span className="text-[var(--accent-blue)] font-medium" data-id="element-1951">
                  legal@metroagency.co
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="col-span-8 space-y-6" data-id="element-1952">
          {[{
          n: '01',
          t: 'Sobre Metro Agency',
          p: 'Metro Agency S.L., con NIF B12345678 y domicilio social en Carrer de la Marina, 145, 08013 Barcelona, España, es la entidad responsable de los servicios contratados a través de este sitio. Inscrita en el Registro Mercantil de Barcelona. [ Texto legal pendiente de validación por asesoría. ]'
        }, {
          n: '02',
          t: 'Servicios contratados',
          p: 'Los packs descritos en metroagency.co/packs constituyen una oferta cerrada con alcance, plazo y precio especificados. La contratación implica la aceptación íntegra de los presentes términos. [ Detalle pendiente sobre tipos de servicio, perímetro de cada pack, exclusiones generales. ]'
        }, {
          n: '03',
          t: 'Condiciones de pago',
          p: 'Todos los precios mostrados incluyen IVA al tipo general del 21% conforme a la Ley 37/1992. El pago se estructura habitualmente en dos hitos — 50% al inicio del proyecto y 50% antes de la entrega final. Métodos aceptados: transferencia bancaria SEPA y tarjeta a través de Stripe. [ Detalle pendiente: plazos exactos, intereses de demora aplicables conforme a Ley 3/2004, recargo por impago. ]'
        }, {
          n: '04',
          t: 'Plazos y entregas',
          p: 'Los plazos indicados en cada pack son estimaciones en días laborables sujetas a la entrega puntual de contenidos, accesos y aprobaciones por parte del cliente. Cualquier retraso imputable al cliente prorrogará el calendario en igual medida. [ Detalle pendiente sobre causas de fuerza mayor. ]'
        }, {
          n: '05',
          t: 'Revisiones y cambios',
          p: 'Cada pack incluye un número específico de rondas de revisión. Las revisiones adicionales se facturan como add-on al precio vigente. Los cambios fuera del alcance original requieren una orden de cambio firmada. [ Detalle pendiente sobre qué constituye "fuera de alcance" vs. "ajuste razonable". ]'
        }, {
          n: '06',
          t: 'Política de devoluciones',
          p: 'Conforme al artículo 103.a del Real Decreto Legislativo 1/2007 (Ley General para la Defensa de los Consumidores), los servicios digitales personalizados quedan excluidos del derecho de desistimiento una vez iniciada la ejecución. Para servicios no iniciados se aplica el derecho de desistimiento de 14 días naturales. [ Detalle pendiente: condiciones específicas por pack, devoluciones parciales, depósito no reembolsable. ]'
        }, {
          n: '07',
          t: 'Garantía post-entrega',
          p: 'Se aplica una garantía de 14 días naturales desde la fecha de entrega para corregir defectos imputables al trabajo realizado por Metro Agency S.L. La garantía no cubre errores derivados de modificaciones posteriores realizadas por el cliente o terceros. [ Texto legal pendiente. ]'
        }, {
          n: '08',
          t: 'Propiedad intelectual',
          p: 'Los derechos de propiedad intelectual sobre los entregables finales se transfieren al cliente en el momento del pago íntegro del proyecto. Metro Agency S.L. conserva el derecho a utilizar el trabajo realizado en su portfolio y materiales de comunicación, salvo pacto contrario por escrito. [ Detalle pendiente sobre licencias de software, fuentes, imágenes con licencia. ]'
        }, {
          n: '09',
          t: 'Confidencialidad',
          p: '[ Texto pendiente: tratamiento de información sensible compartida durante el proyecto. NDA implícito por defecto. Duración del compromiso post-proyecto. ]'
        }, {
          n: '10',
          t: 'Cancelaciones',
          p: '[ Texto pendiente: condiciones de cancelación por parte del cliente, importes no reembolsables, facturación del trabajo realizado hasta la fecha, indemnización por resolución unilateral. ]'
        }, {
          n: '11',
          t: 'Limitación de responsabilidad',
          p: '[ Texto pendiente: límite de responsabilidad al importe efectivamente facturado, exclusión de daños indirectos, lucro cesante y pérdida de oportunidad. ]'
        }, {
          n: '12',
          t: 'Protección de datos · RGPD',
          p: 'El tratamiento de datos personales se realiza conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018 (LOPDGDD). Los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad pueden ejercerse escribiendo a privacy@metroagency.co. [ Texto completo pendiente. ]'
        }, {
          n: '13',
          t: 'Cookies',
          p: '[ Política de cookies pendiente — cookies técnicas, de analítica, de marketing. Configuración disponible en el banner del sitio. ]'
        }, {
          n: '14',
          t: 'Modificaciones',
          p: '[ Metro Agency se reserva el derecho a modificar los presentes términos. Las modificaciones se notifican con antelación a los clientes con proyectos en curso. ]'
        }, {
          n: '15',
          t: 'Jurisdicción aplicable',
          p: 'Las partes, con renuncia expresa a cualquier otro fuero, se someten a los Juzgados y Tribunales de Barcelona para la resolución de cualquier controversia derivada de la relación contractual.'
        }].map(s => <div key={s.n} className="border-t border-[var(--divider)] pt-4" data-id="element-1953">
              <div className="flex items-baseline gap-3 mb-2" data-id="element-1954">
                <span className="font-mono text-[10px] font-bold text-[var(--accent-blue)] tracking-widest" data-id="element-1955">
                  {s.n}
                </span>
                <div className="font-space font-bold text-lg leading-tight tracking-tight" data-id="element-1956">
                  {s.t}
                </div>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-1957">
                {s.p}
              </div>
            </div>)}

          {/* Acceptance */}
          <div className="text-white p-5 mt-4" style={{
          background: '#1A2BFF'
        }} data-id="element-1958">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-1959">
              ACEPTACIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-1960">
              La contratación de cualquier pack implica la aceptación de estos
              términos.
            </div>
            <div className="text-[11px] opacity-90 leading-relaxed" data-id="element-1961">
              Si algo no te encaja antes de firmar, escríbenos a
              legal@metroagency.co y lo hablamos. Preferimos resolver dudas en
              la conversación que en una factura.
            </div>
          </div>
        </div>
      </div>

      <SiteFooter data-id="element-1962" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  Page section wrapper with notes                                    */
/* ================================================================== */
function PageRedesignBlock({
  number,
  title,
  url,
  intro,
  notes,
  mockup
}: {
  number: string;
  title: string;
  url: string;
  intro: string;
  notes: {
    tag: string;
    title: string;
    body: string;
  }[];
  mockup: React.ReactNode;
}) {
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-id="element-1963">
      <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6" data-id="element-1964">
        <div data-id="element-1965">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-3" data-id="element-1966">
            {number}
          </div>
          <h3 className="font-space text-4xl font-bold tracking-tight leading-[0.95] mb-2" data-id="element-1967">
            {title}
          </h3>
          <div className="font-mono text-xs text-[var(--accent-blue)] tracking-wider" data-id="element-1968">
            {url}
          </div>
          <p className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed mt-4" data-id="element-1969">
            {intro}
          </p>
        </div>

        <div className="space-y-3" data-id="element-1970">
          <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest" data-id="element-1971">
            CAMBIOS CLAVE
          </div>
          {notes.map((n, i) => <div key={i} className="border-l-2 border-[var(--accent-blue)] pl-4 py-1" data-id="element-1972">
              <div className="flex items-center gap-2 mb-1" data-id="element-1973">
                <span className="font-mono text-[9px] text-[var(--accent-blue)] font-bold tracking-widest" data-id="element-1974">
                  {n.tag}
                </span>
              </div>
              <div className="font-space font-medium text-sm leading-tight mb-1" data-id="element-1975">
                {n.title}
              </div>
              <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed" data-id="element-1976">
                {n.body}
              </div>
            </div>)}
        </div>
      </div>

      <div className="lg:col-span-7" data-id="element-1977">{mockup}</div>
    </div>;
}
/* ================================================================== */
/*  Section                                                            */
/* ================================================================== */
export function WebsiteRedesignSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="website" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1978">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1979">
        <div className="mb-16 max-w-3xl" data-id="element-1980">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1981">
            10 / WEBSITE REDESIGN
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1982">
            The brand, online.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-1983">
            Las tres páginas principales del sitio reconstruidas en el nuevo
            sistema. Voz 2026 en castellano, paleta y tipografía actualizadas,
            CTAs nuevas, y la metáfora del metro deepened — sin "magia", sin
            "wow", sin exclamaciones forzadas. Pensado para Squarespace: solo
            bloques estándar (hero, columnas, accordion, formulario, footer)
            para que sea editable sin código.
          </p>
        </div>

        {/* Constraints strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-1984">
          {[{
          t: 'Idioma principal',
          v: 'Castellano',
          d: 'Inglés como secundario · CTAs en EN cuando funcionan mejor'
        }, {
          t: 'Sistema',
          v: 'Squarespace',
          d: 'Bloques estándar, sin custom code'
        }, {
          t: 'Paleta',
          v: 'Blue · Ink · Cool grey',
          d: 'Secundarios solo en gráficos de servicios'
        }, {
          t: 'Voz',
          v: '2026',
          d: 'Sin "magic" · sin "wow" · puntos en lugar de exclamaciones'
        }].map(c => <div key={c.t} className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)]" data-id="element-1985">
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1986">
                {c.t.toUpperCase()}
              </div>
              <div className="font-space font-bold text-base leading-tight mb-1" data-id="element-1987">
                {c.v}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1988">
                {c.d}
              </div>
            </div>)}
        </div>

        <div className="space-y-24" data-id="element-1989">
          {/* 10.1 — Home */}
          <PageRedesignBlock number="10.1" title="Inicio" url="metroagency.co" intro="La home pasa del crema con mapa de metro a un sistema más sobrio: gris frío como fondo, mapa traducido a red de servicios (estrategia / creatividad / producción / performance), y la nueva tagline en castellano como pieza central." notes={[{
          tag: 'HERO',
          title: '"Donde las ideas conectan."',
          body: 'La tagline 2026 sustituye al texto largo anterior. Eyebrow corto en azul, párrafo de apoyo en gris para que el titular respire.'
        }, {
          tag: 'CTA',
          title: '"Build something that moves people"',
          body: 'CTA hero — la única que mantiene inglés porque tiene doble lectura (mover personas + tránsito). El secundario en castellano: "Ver trabajos".'
        }, {
          tag: 'VISUAL',
          title: 'Red de servicios, no mapa decorativo',
          body: 'Las cuatro líneas representan estrategia, creatividad, producción y performance. Las estaciones son donde se cruzan.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque azul a sangre',
          body: 'Las 80 palabras del manifiesto se introducen aquí en versión corta. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'CIERRE',
          title: '"Start the conversation"',
          body: 'CTA final más blanda que la del hero. Sin formularios largos en home — solo invitación.'
        }]} mockup={<HomePageMockup data-id="element-1991" />} data-id="element-1990" />

          {/* 10.2 — Services */}
          <PageRedesignBlock number="10.2" title="Servicios" url="metroagency.co/servicios" intro="El hero azul se mantiene como en la versión actual (era lo más fuerte del sitio), pero la copy cambia a la metáfora de línea/estaciones. Los tres bloques actuales se reordenan en cuatro estaciones de servicio y los accordion se convierten en cards expandibles con checklist." notes={[{
          tag: 'HERO',
          title: '"Una línea. Cuatro estaciones."',
          body: 'Mantiene el azul a sangre del actual pero la copy se ajusta al sistema verbal nuevo. Eyebrow + headline + subhead corto.'
        }, {
          tag: 'ESTRUCTURA',
          title: 'De 3 accordion a 3 cards con checklist',
          body: 'Squarespace no permite UX complejo en accordion, así que cada categoría es una card con número grande + título + sublistado siempre visible.'
        }, {
          tag: 'CATEGORÍAS',
          title: 'Creatividad · Marketing & Performance · Producción',
          body: 'Mismas tres familias que el sitio actual, pero renombradas para alinear con el manifesto. Cada una con 4 bullets concretos.'
        }, {
          tag: 'APPROACH',
          title: 'Bloque "Cómo trabajamos"',
          body: 'Tres cards horizontales con los diferenciadores: un equipo, un contacto, un lenguaje. Refuerza la propuesta end-to-end sin usar la palabra "integral".'
        }, {
          tag: 'CTA',
          title: '"Map your next campaign"',
          body: 'El CTA del set 2026 más relevante para esta página — sugiere planificación, no venta. Termina la página sin presión.'
        }]} mockup={<ServicesPageMockup data-id="element-1993" />} data-id="element-1992" />

          {/* 10.3 — About */}
          <PageRedesignBlock number="10.3" title="Nosotros" url="metroagency.co/nosotros" intro="La página About reemplaza el círculo decorativo del logo con un manifiesto a sangre en negro. Recupera la historia de fundación (Barcelona, dificultad para encontrar una agencia end-to-end) y la combina con el sistema verbal nuevo, la matriz de equipo y los números." notes={[{
          tag: 'HERO',
          title: '"Construimos la infraestructura."',
          body: 'Reemplaza "Who are we" por una afirmación de qué hacemos, no de qué somos. La historia de fundación pasa a párrafo de apoyo.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque negro a sangre',
          body: 'Las 80 palabras completas aparecen aquí — es la página donde el manifesto vive en su forma larga. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'VOZ',
          title: 'Tabla usamos / nunca',
          body: 'Bloque visual con las palabras que sí y no usamos. Hace que la política de voz sea pública — útil para clientes y para colaboradores externos.'
        }, {
          tag: 'EQUIPO',
          title: 'Grid 4 columnas',
          body: 'Foto + nombre + rol. Sin bios largas. Squarespace lo permite con bloque image grid estándar.'
        }, {
          tag: 'NÚMEROS',
          title: '"Estaciones por las que hemos pasado"',
          body: 'Reframe de los típicos números de agencia (campañas, marcas, sectores, idiomas) bajo la metáfora de tránsito.'
        }, {
          tag: 'CTA',
          title: '"Let\u2019s connect the dots"',
          body: 'CTA azul a sangre. La invitación más suave del set — para una página que cierra contando quiénes somos, no vendiendo nada.'
        }]} mockup={<AboutPageMockup data-id="element-1995" />} data-id="element-1994" />

          {/* 10.4 — Packs */}
          <PageRedesignBlock number="10.4" title="Packs" url="metroagency.co/packs" intro="Página de packs conectada desde Servicios — cuatro tiers (Micro, Essentials, Professional, Premium) más una sección de add-ons. Precios con IVA incluido para transparencia. Tier 0 deliberadamente limitado como puerta de entrada; cada pack tiene CTA directo de compra y opción de agendar llamada de 15 min." notes={[{
          tag: 'HERO',
          title: 'Cuatro tiers visibles arriba',
          body: 'Strip con los 4 tiers (Micro / Essentials / Professional / Premium) inmediatamente debajo del titular — orientación rápida antes de hacer scroll.'
        }, {
          tag: 'TIER 0',
          title: 'Micro tratado como puerta de entrada',
          body: 'Se presenta solo, con limitaciones explícitas en naranja. Filosofía clara: pack barato para eliminar la objeción de precio, con fricción que empuja al upgrade.'
        }, {
          tag: 'PRECIO',
          title: 'IVA incluido siempre visible',
          body: 'Cada precio lleva la etiqueta "IVA INCL." en mono. Sin asteriscos, sin sorpresas. La pregunta sobre IVA está la primera del FAQ.'
        }, {
          tag: 'COMBOS',
          title: 'Badge "COMBO" con ahorro real',
          body: 'Brand + Web Combo y Full System llevan badge y línea de ahorro. El ahorro está en euros, no en porcentaje vago.'
        }, {
          tag: 'ADD-ONS',
          title: 'Tabla negra con etiqueta "RECURRENTE"',
          body: 'Add-ons mensuales (SEO, social, mantenimiento) van marcados con badge "RECURRENTE" para que el cliente no se sorprenda con cuota.'
        }, {
          tag: 'CTA STICKY',
          title: 'Bloque negro con "Agendar llamada · 15 min"',
          body: 'Sidebar fija en la sección FAQ. Para quien todavía no sabe qué pack comprar — empuja a conversación corta, no a formulario largo.'
        }]} mockup={<PacksPageMockup data-id="element-1997" />} data-id="element-1996" />

          {/* 10.5 — Terms */}
          <PageRedesignBlock number="10.5" title="Términos y condiciones" url="metroagency.co/terminos" intro="Página legal con 15 secciones desde datos de la empresa hasta jurisdicción aplicable. Contenido con placeholders [ Texto pendiente ] mientras se valida con asesoría legal. Layout de índice navegable + contenido en columna estándar — bloque Squarespace de dos columnas con índice anchor." notes={[{
          tag: 'ESTRUCTURA',
          title: '15 secciones numeradas',
          body: 'De "Sobre Metro Agency" a "Jurisdicción". Incluye lo que el usuario pidió: devoluciones (06), garantía post-entrega (07), condiciones de pago (03), cancelaciones (10), más los estándar (RGPD, IP, jurisdicción).'
        }, {
          tag: 'ÍNDICE',
          title: 'TOC fijo a la izquierda',
          body: 'Anchors clicables. Sección actual marcada en azul. Mantiene la página navegable aunque sea larga.'
        }, {
          tag: 'PLACEHOLDERS',
          title: '[ Texto pendiente ] visible',
          body: 'Cada sección que necesita validación de asesoría aparece marcada explícitamente. Las secciones legalmente referenciables (Ley 37/1992, RD 1/2007, RGPD) ya citan la normativa.'
        }, {
          tag: 'ACEPTACIÓN',
          title: 'Bloque azul al cierre',
          body: 'Recuerda que contratar = aceptar, e invita a escribir a legal@metroagency.co antes de firmar si algo no encaja. Tono Metro, no tono abogado.'
        }, {
          tag: 'METADATA',
          title: 'Última actualización + versión',
          body: 'Fecha y versión visibles. Estándar legal cuando los términos cambian con el tiempo.'
        }]} mockup={<TermsPageMockup data-id="element-1999" />} data-id="element-1998" />
        </div>

        {/* Sitemap notes */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2000">
          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-2001">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2002">
              NAVEGACIÓN
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-2003">
              Cinco entradas. Una salida.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-2004">
              {[['Inicio', 'Punto de entrada. Manifiesto corto + red de servicios + selected work.'], ['Servicios', 'Cuatro estaciones. Listado completo + cómo trabajamos.'], ['Packs', 'Cuatro tiers + add-ons. Compra directa o agendar llamada.'], ['Nosotros', 'Manifiesto largo + equipo + números.'], ['Contacto', 'Form mínimo. Nombre, email, mensaje. Nada más.'], ['Términos', 'Página legal con 15 secciones. Link en footer.']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-2005">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-2006">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-2007">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-2008">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2009">
              CONSTRUCCIÓN · SQUARESPACE
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-2010">
              Solo bloques estándar.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-2011">
              {[['Hero', 'Section con background color + título + texto + button'], ['Servicios', 'Cards section con 3-4 columnas + icon block'], ['Manifesto', 'Section con background ink/blue + texto a sangre'], ['Trabajos', 'Image gallery grid + link'], ['Equipo', 'People block o image grid 4 cols'], ['Footer', 'Footer estándar Squarespace con 4 columnas']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-2012">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-2013">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-2014">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}